import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Download, MapPin, Star, MessageSquare, Globe, Mail, 
  Phone, Building2, Filter, Loader2, CheckCircle, XCircle, LogOut,
  History, ChevronDown
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const LeadFinder = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // Form state
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [minRating, setMinRating] = useState(0);
  const [minReviews, setMinReviews] = useState(0);
  const [hasWebsite, setHasWebsite] = useState(null);
  const [hasEmail, setHasEmail] = useState(null);
  
  // Results state
  const [scanning, setScanning] = useState(false);
  const [progress, setProgress] = useState('');
  const [results, setResults] = useState(null);
  const [scanHistory, setScanHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [error, setError] = useState('');

  const categories = [
    'Restaurant', 'Cafe', 'Barber', 'Friseur', 'Zahnarzt', 'Rechtsanwalt',
    'Steuerberater', 'Immobilienmakler', 'Autowerkstatt', 'Fitnessstudio',
    'Bäckerei', 'Apotheke', 'Hotel', 'Arzt', 'Physiotherapie'
  ];

  useEffect(() => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
    } else {
      setIsAuthenticated(true);
      loadHistory();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    return { Authorization: `Basic ${auth}` };
  };

  const loadHistory = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/leads/history`, {
        headers: getAuthHeader()
      });
      setScanHistory(response.data.scans || []);
    } catch (err) {
      console.error('Failed to load history:', err);
    }
  };

  const handleScan = async (e) => {
    e.preventDefault();
    if (!city || !category) {
      setError('Please enter city and category');
      return;
    }

    setScanning(true);
    setError('');
    setResults(null);
    setProgress('Initializing scan...');

    try {
      setProgress('Scanning page 1...');
      await new Promise(r => setTimeout(r, 500));
      setProgress('Scanning page 2...');
      await new Promise(r => setTimeout(r, 500));
      setProgress('Scanning page 3...');
      await new Promise(r => setTimeout(r, 500));
      setProgress('Extracting emails from websites...');

      const response = await axios.post(
        `${BACKEND_URL}/api/admin/leads/scan`,
        {
          city,
          category,
          min_rating: minRating,
          min_reviews: minReviews,
          has_website: hasWebsite,
          has_email: hasEmail
        },
        { headers: getAuthHeader() }
      );

      setProgress('Scan complete!');
      setResults(response.data);
      loadHistory();
    } catch (err) {
      setError(err.response?.status === 401 ? 'Session expired. Please login again.' : 'Scan failed. Please try again.');
      if (err.response?.status === 401) {
        sessionStorage.removeItem('adminAuth');
        navigate('/admin');
      }
    } finally {
      setScanning(false);
    }
  };

  const handleExport = async (scanId) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/admin/leads/export/${scanId}`,
        { 
          headers: getAuthHeader(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `leads_${city}_${category}_${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError('Export failed');
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="min-h-screen bg-black">
      {/* Header */}
      <header className="border-b border-white/10 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#B8860B]/10 rounded-lg flex items-center justify-center">
              <Search className="w-5 h-5 text-[#B8860B]" />
            </div>
            <div>
              <h1 className="text-white font-medium">Lead Finder</h1>
              <p className="text-[#A1A1AA] text-xs">Google Maps Business Scanner</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setShowHistory(!showHistory)}
              className="flex items-center space-x-2 text-[#A1A1AA] hover:text-white transition-colors"
            >
              <History size={18} />
              <span className="text-sm">History</span>
            </button>
            <button
              onClick={handleLogout}
              className="flex items-center space-x-2 text-red-400 hover:text-red-300 transition-colors"
            >
              <LogOut size={18} />
              <span className="text-sm">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Filters Panel */}
          <div className="lg:col-span-1">
            <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
              <div className="flex items-center space-x-2 mb-6">
                <Filter className="w-5 h-5 text-[#B8860B]" />
                <h2 className="text-white font-medium">Scan Filters</h2>
              </div>

              <form onSubmit={handleScan} className="space-y-5">
                {/* City */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    <MapPin size={14} className="inline mr-1" /> City
                  </label>
                  <input
                    type="text"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="e.g. Berlin, München, Hamburg"
                    className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-gray-600 focus:outline-none focus:border-[#B8860B] transition-colors"
                    data-testid="filter-city"
                  />
                </div>

                {/* Category */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    <Building2 size={14} className="inline mr-1" /> Business Category
                  </label>
                  <div className="relative">
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#B8860B] transition-colors appearance-none"
                      data-testid="filter-category"
                    >
                      <option value="">Select category</option>
                      {categories.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" size={18} />
                  </div>
                </div>

                {/* Rating */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    <Star size={14} className="inline mr-1" /> Minimum Rating
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="5"
                    step="0.5"
                    value={minRating}
                    onChange={(e) => setMinRating(parseFloat(e.target.value) || 0)}
                    className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#B8860B] transition-colors"
                    data-testid="filter-rating"
                  />
                </div>

                {/* Reviews */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    <MessageSquare size={14} className="inline mr-1" /> Minimum Reviews
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={minReviews}
                    onChange={(e) => setMinReviews(parseInt(e.target.value) || 0)}
                    className="w-full bg-black border border-white/10 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-[#B8860B] transition-colors"
                    data-testid="filter-reviews"
                  />
                </div>

                {/* Has Website */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    <Globe size={14} className="inline mr-1" /> Has Website
                  </label>
                  <div className="flex space-x-2">
                    {[
                      { value: null, label: 'Any' },
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' }
                    ].map(opt => (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => setHasWebsite(opt.value)}
                        className={`flex-1 py-2 rounded-lg text-sm transition-colors ${
                          hasWebsite === opt.value
                            ? 'bg-[#B8860B] text-black'
                            : 'bg-black border border-white/10 text-white/60 hover:border-[#B8860B]/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Has Email */}
                <div>
                  <label className="block text-white/60 text-sm mb-2">
                    <Mail size={14} className="inline mr-1" /> Has Email
                  </label>
                  <div className="flex space-x-2">
                    {[
                      { value: null, label: 'Any' },
                      { value: true, label: 'Yes' },
                      { value: false, label: 'No' }
                    ].map(opt => (
                      <button
                        key={String(opt.value)}
                        type="button"
                        onClick={() => setHasEmail(opt.value)}
                        className={`flex-1 py-2 rounded-lg text-sm transition-colors ${
                          hasEmail === opt.value
                            ? 'bg-[#B8860B] text-black'
                            : 'bg-black border border-white/10 text-white/60 hover:border-[#B8860B]/50'
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>

                {error && (
                  <div className="text-red-500 text-sm">{error}</div>
                )}

                <button
                  type="submit"
                  disabled={scanning}
                  className="w-full bg-[#B8860B] hover:bg-[#9A7209] text-black font-medium py-3 rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center space-x-2"
                  data-testid="start-scan-btn"
                >
                  {scanning ? (
                    <>
                      <Loader2 className="w-5 h-5 animate-spin" />
                      <span>Scanning...</span>
                    </>
                  ) : (
                    <>
                      <Search className="w-5 h-5" />
                      <span>Start Scan</span>
                    </>
                  )}
                </button>
              </form>

              {/* Progress */}
              {scanning && (
                <div className="mt-4 p-4 bg-black rounded-lg border border-[#B8860B]/30">
                  <div className="flex items-center space-x-2 text-[#B8860B]">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">{progress}</span>
                  </div>
                </div>
              )}
            </div>

            {/* Scan History */}
            {showHistory && scanHistory.length > 0 && (
              <div className="mt-6 bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
                <h3 className="text-white font-medium mb-4">Recent Scans</h3>
                <div className="space-y-3">
                  {scanHistory.slice(0, 5).map(scan => (
                    <div key={scan.id} className="p-3 bg-black rounded-lg border border-white/5">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-white text-sm">{scan.category} in {scan.city}</p>
                          <p className="text-[#A1A1AA] text-xs">{scan.filtered_count} leads</p>
                        </div>
                        <button
                          onClick={() => handleExport(scan.id)}
                          className="text-[#B8860B] hover:text-[#D4AF37] p-2"
                        >
                          <Download size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Results Panel */}
          <div className="lg:col-span-2">
            {results ? (
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-6">
                {/* Results Header */}
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className="text-white font-medium">Scan Results</h2>
                    <p className="text-[#A1A1AA] text-sm">
                      Found {results.total_found} businesses, {results.filtered_count} match filters
                    </p>
                  </div>
                  <button
                    onClick={() => handleExport(results.scan_id)}
                    className="flex items-center space-x-2 bg-[#B8860B] hover:bg-[#9A7209] text-black px-4 py-2 rounded-lg transition-colors"
                    data-testid="export-btn"
                  >
                    <Download size={18} />
                    <span>Download Excel</span>
                  </button>
                </div>

                {/* Results Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-white/10">
                        <th className="text-left text-[#A1A1AA] text-xs font-medium py-3 px-2">Name</th>
                        <th className="text-left text-[#A1A1AA] text-xs font-medium py-3 px-2">Rating</th>
                        <th className="text-left text-[#A1A1AA] text-xs font-medium py-3 px-2">Reviews</th>
                        <th className="text-left text-[#A1A1AA] text-xs font-medium py-3 px-2">Phone</th>
                        <th className="text-left text-[#A1A1AA] text-xs font-medium py-3 px-2">Website</th>
                        <th className="text-left text-[#A1A1AA] text-xs font-medium py-3 px-2">Email</th>
                      </tr>
                    </thead>
                    <tbody>
                      {results.leads.map((lead, idx) => (
                        <tr key={idx} className="border-b border-white/5 hover:bg-white/5">
                          <td className="py-3 px-2">
                            <p className="text-white text-sm">{lead.name}</p>
                            <p className="text-[#A1A1AA] text-xs">{lead.address}</p>
                          </td>
                          <td className="py-3 px-2">
                            <span className="text-[#B8860B] text-sm flex items-center">
                              <Star size={12} className="mr-1" fill="#B8860B" />
                              {lead.rating}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-white text-sm">{lead.reviews}</td>
                          <td className="py-3 px-2">
                            {lead.phone ? (
                              <a href={`tel:${lead.phone}`} className="text-[#B8860B] text-sm flex items-center">
                                <Phone size={12} className="mr-1" />
                                {lead.phone}
                              </a>
                            ) : (
                              <span className="text-gray-600 text-sm">-</span>
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {lead.website ? (
                              <CheckCircle size={16} className="text-green-500" />
                            ) : (
                              <XCircle size={16} className="text-red-500" />
                            )}
                          </td>
                          <td className="py-3 px-2">
                            {lead.email ? (
                              <a href={`mailto:${lead.email}`} className="text-[#B8860B] text-sm">
                                {lead.email}
                              </a>
                            ) : (
                              <span className="text-gray-600 text-sm">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {results.leads.length === 0 && (
                  <div className="text-center py-12">
                    <p className="text-[#A1A1AA]">No leads match your filters</p>
                  </div>
                )}
              </div>
            ) : (
              <div className="bg-[#0A0A0A] border border-white/10 rounded-xl p-12 text-center">
                <div className="w-16 h-16 bg-[#B8860B]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-[#B8860B]" />
                </div>
                <h3 className="text-white font-medium mb-2">Ready to Scan</h3>
                <p className="text-[#A1A1AA] text-sm max-w-md mx-auto">
                  Enter a city and business category, then click "Start Scan" to find potential leads from Google Maps.
                </p>
                <div className="mt-6 p-4 bg-black rounded-lg border border-[#B8860B]/20 max-w-md mx-auto">
                  <p className="text-[#B8860B] text-xs">
                    💡 Tip: Filter by "No Website" to find businesses that need your web design services.
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LeadFinder;
